import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { WizardCta, WizardStep } from '@/lib/base/wizard';
import { Wizard } from '@/lib/base/wizard';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { useAssociationMatchers } from '@/lib/crm/hooks/useAssociationMatchers';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { useStoreObjects } from '@/lib/crm/store/hooks';
import type { CrmObject } from '@/types/crm';

const DESTRUCTIVE_PRIMARY_CTA: WizardCta = {
  label: 'Merge records',
  variant: 'primary-destructive',
};

const DISCARD_CONFIRMATION = {
  title: 'Discard merge?',
  description: 'Close the merge wizard without merging?',
  confirmLabel: 'Discard',
  cancelLabel: 'Keep merging',
};

import { MergeStep1PickCanonical } from './MergeStep1PickCanonical';
import { MergeStep2ResolveProperties } from './MergeStep2ResolveProperties';
import { MergeStep3ReviewConfirm } from './MergeStep3ReviewConfirm';
import { MergeWizardProvider, useMergeWizard } from './MergeWizardContext';
import { useMergeSubmit } from './useMergeSubmit';

export interface MergeWizardProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  entityTypeName: EntityTypeName;
  entityIds: [string, string];
  /**
   * Called after a merge completes successfully (before the wizard closes).
   * The list page uses this to clear the now-stale selection — the merged-away
   * row is gone, so leaving it selected would inflate the bulk-action count.
   */
  onMergeComplete?: (canonicalEntityId: string) => void;
}

export function MergeWizard(props: MergeWizardProps) {
  return (
    <MergeWizardProvider>
      <MergeWizardInner {...props} />
    </MergeWizardProvider>
  );
}

function MergeWizardInner({
  isOpen,
  onOpenChange,
  orgId,
  entityTypeName,
  entityIds,
  onMergeComplete,
}: MergeWizardProps) {
  const { canonicalId, setSourceId, reset, clearPropertyResolutions } =
    useMergeWizard();

  const [fetchedById, setFetchedById] = useState<Record<string, CrmObject>>({});
  const [isLoading, setIsLoading] = useState(false);
  const prevCanonicalRef = useRef<string | null>(null);

  const storeObjects = useStoreObjects(entityIds);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    const client = createCRMObjectClient(orgId, entityTypeName);
    client
      .getByEntityIds(entityIds, { includeAssociations: true })
      .then(result => {
        if (!cancelled) {
          setFetchedById(result);
        }
      })
      .catch(() => {
        // Swallow — step body renders the available data (skeleton/empty placeholder).
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, orgId, entityTypeName, entityIds]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setFetchedById({});
    }
  }, [isOpen, reset]);

  useEffect(() => {
    if (canonicalId && entityIds.includes(canonicalId)) {
      const other = entityIds.find(id => id !== canonicalId) ?? null;
      setSourceId(other);
      if (
        prevCanonicalRef.current !== null &&
        prevCanonicalRef.current !== canonicalId
      ) {
        clearPropertyResolutions();
      }
    }
    prevCanonicalRef.current = canonicalId ?? null;
  }, [canonicalId, entityIds, setSourceId, clearPropertyResolutions]);

  const storeById = useMemo(() => {
    const map: Record<string, CrmObject> = {};
    for (const obj of storeObjects) {
      map[obj.entity_id] = obj;
    }
    return map;
  }, [storeObjects]);

  const leftObject: CrmObject | undefined =
    fetchedById[entityIds[0]] ?? storeById[entityIds[0]];
  const rightObject: CrmObject | undefined =
    fetchedById[entityIds[1]] ?? storeById[entityIds[1]];

  const canonicalObject = canonicalId
    ? (fetchedById[canonicalId] ?? storeById[canonicalId])
    : undefined;
  const sourceEntityId = canonicalId
    ? (entityIds.find(id => id !== canonicalId) ?? null)
    : null;
  const sourceObject = sourceEntityId
    ? (fetchedById[sourceEntityId] ?? storeById[sourceEntityId])
    : undefined;

  const associationsByEntityId = useMemo(() => {
    const map: Record<
      string,
      Array<{ association_type_name: string; target_entity_id: string }>
    > = {};
    for (const id of entityIds) {
      const obj = fetchedById[id] ?? storeById[id];
      if (obj?.associations) {
        map[id] = obj.associations;
      }
    }
    return map;
  }, [entityIds, fetchedById, storeById]);

  const handleSuccess = useCallback(
    (canonicalEntityId: string) => {
      onMergeComplete?.(canonicalEntityId);
      onOpenChange(false);
    },
    [onMergeComplete, onOpenChange]
  );

  const { schema } = usePropertySchema(orgId, entityTypeName);
  const { associationTypes } = useAssociationMatchers(orgId);

  const { submit } = useMergeSubmit({
    orgId,
    entityTypeName,
    entityIds,
    onSuccess: handleSuccess,
    associationsByEntityId,
    canonicalObject,
    sourceObject,
    schema,
    associationTypes,
  });

  const steps: WizardStep[] = useMemo(
    () => [
      {
        id: 'pick-canonical',
        label: 'Pick canonical',
        title: 'Choose the canonical record',
        subtitle:
          'The other record will be merged into the canonical record and then deleted.',
        canAdvance: () => canonicalId !== null,
        render: () => (
          <MergeStep1PickCanonical
            leftObject={leftObject}
            rightObject={rightObject}
            isLoading={isLoading}
            entityTypeName={entityTypeName}
          />
        ),
      },
      {
        id: 'resolve-properties',
        label: 'Resolve properties',
        title: 'Resolve property conflicts',
        subtitle: 'Pick a winning value for each conflict.',
        fillHeight: true,
        maxContentWidth: 1024,
        canAdvance: () => canonicalId !== null && sourceObject !== undefined,
        render: () => (
          <MergeStep2ResolveProperties
            canonicalObject={canonicalObject}
            sourceObject={sourceObject}
            entityTypeName={entityTypeName}
          />
        ),
      },
      {
        id: 'review-confirm',
        label: 'Review',
        title: 'Review and confirm',
        subtitle: 'Confirm the records to merge.',
        primaryCta: DESTRUCTIVE_PRIMARY_CTA,
        canAdvance: () => canonicalId !== null && sourceObject !== undefined,
        render: () => (
          <MergeStep3ReviewConfirm
            canonicalObject={canonicalObject}
            sourceObject={sourceObject}
            entityTypeName={entityTypeName}
          />
        ),
      },
    ],
    [
      canonicalId,
      leftObject,
      rightObject,
      isLoading,
      sourceObject,
      canonicalObject,
      entityTypeName,
    ]
  );

  return (
    <Wizard
      mode='fullscreen'
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      steps={steps}
      isDirty={canonicalId !== null}
      discardConfirmation={DISCARD_CONFIRMATION}
      dialogAriaLabel='Merge records'
      onConfirm={submit}
      // On mobile the header X overlays the step indicator, so drop it there
      // and give users an explicit footer Cancel as the exit affordance.
      showCancelButton
      fullscreenProps={{ hideCloseButtonOnMobile: true }}
    />
  );
}
